<script setup>
import { Head } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
import LegalPageLayout from '@/Layouts/LegalPageLayout.vue';
import LegalList from '@/Components/LegalList.vue';
import LegalSection from '@/Components/LegalSection.vue';

defineProps({
    title: {
        type: String,
        default: 'Terms of Service'
    }
});
</script>

<template>

    <Head :title="title" />
    <LegalPageLayout :title="title">
        <p class="mt-4">Last updated: October 1, 2024</p>

        <LegalSection title="Acceptance of the Terms of Service">
            <p>
                These terms of service are entered into between you and MMT Dev LLC (the
                <span class="font-semibold">“Company</span>
                ,”
                <span class="font-semibold">“we</span>
                ,” or
                <span class="font-semibold">“us”</span>
                ). The following terms and any documents they reference govern your
                access to and use of
                <a href="https://spicycollab.com" class="font-semibold text-malibu hover:underline">
                    SpicyCollab.com
                </a>
                , including any content, functionality, and services offered on or
                through
                <a href="https://spicycollab.com" class="font-semibold text-malibu hover:underline">
                    SpicyCollab.com
                </a>
                (the
                <span class="font-semibold">“Website”</span>
                ), whether as a guest or a registered user.
            </p>
            <p>
                Please read the terms of service carefully before you start to use the
                Website. By using the Website or by clicking to accept or agree to the
                terms of service when this option is made available to you, you accept
                and agree to be bound and abide by these terms of service. If you do not
                want to agree to these terms of service, you must not access or use the
                Website.
            </p>
            <!-- TODO: ADD LINKS -->
            <p class="p-2 border-2 border-black rounded">
                These terms of service contain provisions that govern how claims you and
                we have against each other are resolved (see “
                <Link href="#warrant-disclaimers" class="font-semibold text-malibu hover:underline">
                Warranty Disclaimers
                </Link>
                ,” “
                <Link href="#limitation-of-liability" class="font-semibold text-malibu hover:underline">
                Limitation of Liability
                </Link>
                ,” and “
                <Link href="#resolving-disputes" class="font-semibold text-malibu hover:underline">
                Resolving Disputes
                </Link>
                ”). It also contains an arbitration and class action waiver (see “
                <Link href="#arbitration" class="font-semibold text-malibu hover:underline">
                Arbitration and Class Action Waiver
                </Link>
                ”) (the
                <span class="font-semibold">“Arbitration Agreement”</span>
                ). Except for certain types of disputes described in the Arbitration
                Agreement, you and we agree that any disputes arising out of these terms
                of service or the Website will be resolved through binding arbitration.
                By accepting these terms of service, you and we are each waiving the
                right to a trial by jury or representative proceeding or to participate
                in any class action lawsuit or class-wide arbitration.
            </p>
            <p>
                This Website is offered and available to users at least 18 years of age
                or older. By using this Website, you state that you are of legal age to
                form a binding contract with the Company and meet all the preceding
                eligibility requirements. If you do not meet all these requirements, you
                must not access or use the Website.
            </p>
        </LegalSection>

        <LegalSection title="Changes to the Terms of Service">
            <p>
                We may revise and update these terms of service on one or more
                occasions. All changes are effective immediately when we post them and
                apply to all access to and use of the Website afterward. But any changes
                to the dispute resolution provisions set out in
                <Link href="#resolving-disputes" class="font-semibold text-malibu hover:underline">
                Resolving Disputes
                </Link>
                will not apply to any disputes for which the parties have actual notice
                on or before the date the change is posted on the Website.
            </p>
            <p>
                Your continued use of the Website after the posting of revised terms of
                service means that you accept and agree to the changes. You are expected
                to check this page frequently so you are aware of any changes, as they
                are binding on you.
            </p>
        </LegalSection>

        <LegalSection title="Nature of Website">
            <p>
                The Website is a public access general purpose portfolio hosting website
                and community for artists to collaborate with each other. The Website
                provides a hosting service for person-to-person display, marketing, and
                communication on a professional level between artists looking to
                collaborate with each other.
            </p>
            <p>
                You acknowledge that the Company (1) is not an employment agency,
                casting agent, talent agent, agency, manager, producer, or a production
                company, (2) does not procure, attempt to procure, get, gain, or retain
                employment, engagements, employment opportunities, auditions, jobs,
                bookings, castings, a talent agent, or manager for you or its users, (3)
                does not manage or direct the developments of the careers, or represent,
                you or its users, and (4) does not guarantee an employment opportunity,
                audition, any amount of income, jobs, or employment from the use of this
                Website. The Company does not offer advice, managerial directions, or
                opinions in any potential collaboration and permits you to submit your
                information for potential collaborations through the Website. The
                information included in postings is provided by the applicable artist
                and the Company is not involved in the decision-making process. You
                further acknowledge that the Company may remove postings that are
                inaccurate, offensive, indecent, objectionable, contain errors or
                omissions, or otherwise violates this agreement in any other manner.
            </p>
            <p class="p-2 border-2 border-black rounded">
                You must not use the Website to engage in, participate in, assist,
                support, or facilitate any act of prostitution, sex trafficking of
                children, or sex trafficking. Any violation of this prohibition will
                result in the termination of your account and the blocking of your IP
                address.
            </p>
            <p>
                The Company may require users to submit proof of age and identity
                through one or more methods, including but not limited to:
            </p>
        </LegalSection>

        <LegalSection title="Accessing the Website and Account Security">
            <p>
                We may withdraw or amend this Website and any service or material we
                provide on the Website without notice. We will not be liable if for any
                reason any part of the Website is unavailable. On one or more occasions,
                we may restrict user access, including registered user access, to some
                parts of the Website, or the entire Website.
            </p>
            <p>You are responsible for both:</p>
            <ol>
                <li>
                    Making all arrangements necessary for you to have access to the
                    Website.
                </li>
                <li>
                    Ensuring that all persons who access the Website through your internet
                    connection are aware of these terms of service and comply with them.
                </li>
            </ol>
            <p>
                To access the Website or some of its resources, you may be asked to
                provide specific registration details or other information. It is a
                condition for using the Website that all the information you provide is
                accurate. All information you provide to register with this Website or
                otherwise, including, but not limited to, through the use of any
                interactive features on the Website, is governed by our
                <Link href="/docs/privacy-policy" class="font-semibold text-malibu hover:underline">
                Privacy Policy
                </Link>
                , and you consent to all actions we take regarding your information
                consistent with our
                <Link href="/docs/privacy-policy" class="font-semibold text-malibu hover:underline">
                Privacy Policy
                </Link>
                .
            </p>
            <p>
                The Company may require users to submit proof of age and identity
                through one or more methods, including but not limited to:
            </p>
            <ol>
                <li>
                    Uploading a valid government-issued photo identification document
                    (e.g., passport, driver’s license).
                </li>
                <li>
                    Verifying identity through third-party services that specialize in
                    identity verification.
                </li>
                <li>Selfie verification or other biometric authentication methods.</li>
            </ol>
            <p>
                By providing this information, you consent to the Company’s collection,
                storage, and processing of your data for the purposes of verifying your
                age and identity. This information will be handled in accordance with
                our
                <Link href="/docs/privacy-policy" class="font-semibold text-malibu hover:underline">
                Privacy Policy
                </Link>
                .
            </p>
            <p>
                If you choose or are provided with a username, password, or any other
                piece of information as part of our security procedures, you must treat
                that information as confidential, and you must not disclose it to any
                other person or entity. You also acknowledge that your account is
                personal to you, and you must not provide any other person with access
                to this Website or portions of it using your username, password, or
                other security information. You must notify us immediately of any
                unauthorized access to or use of your username or password or any other
                security breach. You also must ensure that you sign out from your
                account at the end of each session. You should use caution when
                accessing your account from a public or shared computer so that others
                cannot view or record your password or other personal information.
            </p>
            <p>
                We may disable any username, password, or other identifier, whether
                chosen by you or provided by us, for any reason, including if, in our
                opinion, you have violated any provision of these terms of service.
            </p>
        </LegalSection>

        <LegalSection title="Intellectual Property Rights">
            <p>
                The Website and its entire contents, features, and functionality
                (including but not limited to all information, software, text, displays,
                images, video, and audio, and the design, selection, and arrangement of
                it) are owned by the Company, its licensors, or other providers of that
                material and are protected by United States and international copyright,
                trademark, patent, trade secret, and other intellectual property or
                proprietary rights laws.
            </p>
            <p>
                These terms of service allow you to use the Website for your personal,
                noncommercial use only. You must not reproduce, distribute, modify,
                create derivative works of, publicly display, publicly perform,
                republish, download, store, or transmit any of the material on our
                Website, except as follows:
            </p>
            <ol>
                <li>
                    Your computer may temporarily store copies of those materials in RAM
                    incidental to your accessing and viewing those materials.
                </li>
                <li>
                    You may store files automatically cached by your Web browser for
                    display enhancement purposes.
                </li>
                <li>
                    You may print or download one copy of a reasonable number of pages of
                    the Website for your own personal, noncommercial use and not for
                    further reproduction, publication, or distribution.
                </li>
                <li>
                    If we provide social media features with certain content, you may take
                    those actions as are enabled by those social media features.
                </li>
            </ol>
            <p>You must not:</p>
            <ol>
                <li>Modify copies of any materials from this Website.</li>
                <li>
                    Use any illustrations, photographs, video or audio sequences, or any
                    graphics separately from the accompanying text.
                </li>
                <li>
                    Delete or alter any copyright, trademark, or other proprietary rights
                    notices from copies of materials from this Website.
                </li>
            </ol>
            <p>
                You must not access or use for any commercial purposes any part of the
                Website or any services or materials available through the Website.
            </p>
            <p>
                If you print, copy, modify, download, or otherwise use or provide any
                other person with access to any part of the Website in breach of the
                terms of service, your right to use the Website will stop immediately
                and you must, at our option, return or destroy any copies of the
                materials you have made. No interest in the Website or any content on
                the Website is transferred to you, and the Company reserves all rights
                not granted in these terms of service. Any use of the Website not
                permitted by these terms of service is a breach of these terms of
                service and might violate copyright, trademark, and other laws.
            </p>
        </LegalSection>

        <LegalSection title="Trademarks">
            <p>
                The Company name, the term SPICYGIGS, and all related names, logos,
                product and service names, designs, and slogans are trademarks of the
                Company or its affiliates or licensors. You must not use those marks
                without the Company’s prior written permission. All other names, logos,
                product and service names, designs, and slogans on this Website are the
                trademarks of their respective owners.
            </p>
        </LegalSection>

        <LegalSection title="Prohibited Uses">
            <p>
                You must use the Website only for lawful purposes and in accordance with
                these terms of service. You must not use the Website:
            </p>
            <ol>
                <li>
                    In any way that violates any federal, state, local, or international
                    law or regulation (including, without limitation, any laws regarding
                    the export of data or software to and from the US or other countries).
                </li>
                <li>
                    To exploit, harm, or attempt to exploit or harm minors in any way by
                    exposing them to inappropriate content, asking for personally
                    identifiable information, or otherwise.
                </li>
                <li>
                    To send, knowingly receive, upload, download, use, or reuse any
                    material that does not comply with the
                    <Link href="#content-standards" class="font-semibold text-malibu hover:underline">
                    Content Standards
                    </Link>
                    Content Standard set out in these terms of service.
                </li>
                <li>
                    To transmit, or procure the sending of, any advertising or promotional
                    material, including any “junk mail,” “chain letter,” “spam,” or any
                    other similar solicitation.
                </li>
                <li>
                    To impersonate or try to impersonate the Company, a Company employee,
                    another user, or any other person or entity (including, without
                    limitation, by using email addresses or usernames associated with any
                    of the preceding).
                </li>
                <li>
                    To engage in any other conduct that restricts or inhibits anyone’s use
                    or enjoyment of the Website, or that, as determined by us, might harm
                    the Company or users of the Website, or expose them to liability.
                </li>
            </ol>
            <p>Additionally, you must not:</p>
            <ol>
                <li>
                    Use the Website in any manner that could disable, overburden, damage,
                    or impair the Website or interfere with any other party’s use of the
                    Website, including their ability to engage in real time activities
                    through the Website.
                </li>
                <li>
                    Conduct, facilitate, authorize, or permit any text or data mining or
                    web scraping regarding the Website or any services provided through,
                    or concerning, the Website. This includes using (or permitting,
                    authorizing, or attempting to use) (1) any “robot,” “bot,” “spider,”
                    “scraper,” or other automated device, program, tool, algorithm, code,
                    process, or methodology to access, obtain, copy, monitor, record, or
                    republish any part of the Website or any data, content, information,
                    or services accessed through the Website, or (2) any automated
                    analytical technique aimed at analyzing text and data in digital form
                    to generate information that includes but is not limited to patterns,
                    trends, and correlations.
                </li>
                <li>
                    Use any manual process to monitor or copy any of the material on the
                    Website, or for any other purpose not authorized in these terms of
                    service, without our prior written consent.
                </li>
                <li>
                    Use any device, software, or routine that interferes with the
                    Website’s proper working.
                </li>
                <li>
                    Introduce any viruses, trojan horses, worms, logic bombs, or other
                    material that is malicious or technologically harmful.
                </li>
                <li>
                    Attempt to gain unauthorized access to, interfere with, damage, or
                    disrupt any parts of the Website, the server on which the Website is
                    stored, or any server, computer, or database connected to the Website.
                </li>
                <li>
                    Attack the Website by a denial-of-service attack or a distributed
                    denial-of-service attack.
                </li>
                <li>Otherwise try to interfere with the Website’s proper working.</li>
            </ol>
        </LegalSection>

        <LegalSection title="User Contributions">
            <p>
                The Website may contain message boards, direct messaging, personal web
                pages or profiles, and other interactive features (collectively, the “
                <span class="font-semibold">Interactive Services</span>
                ”) that allow users to post, submit, publish, display, or transmit to
                other users or other persons (
                <span class="font-semibold">“post”</span>
                ) content or materials (collectively, “
                <span class="font-semibold">User Contributions</span>
                ”) on or through the Website.
            </p>
            <p>
                All User Contributions must comply with the
                <Link href="#content-standards" class="font-semibold text-malibu hover:underline">
                Content Standards
                </Link>
                set out in these terms of service.
            </p>
            <p>
                Any User Contribution you post to the Website will be considered
                nonconfidential and nonproprietary. By providing any User Contribution
                on the Website, you hereby grant us and our affiliates and service
                providers, and each of their and our respective licensees, successors,
                and assigns a license to use, reproduce, modify, perform, display,
                distribute, and otherwise disclose to nonparties any User Contribution
                for any purpose.
            </p>
            <p>You state that the following facts are accurate:</p>
            <ol>
                <li>
                    You own or control all rights in and to the User Contributions and
                    have the right to grant the license granted above to us and our
                    affiliates and service providers, and each of their and our respective
                    licensees, successors, and assigns.
                </li>
                <li>
                    All your User Contributions do and will comply with these terms of
                    service.
                </li>
            </ol>
            <p>
                You acknowledge that you are responsible for any User Contributions you
                submit or contribute, and you, not the Company, have full responsibility
                for that content, including its legality, reliability, accuracy, and
                appropriateness.
            </p>
            <p>
                We are not responsible or liable to any nonparty for the content or
                accuracy of any User Contributions you or any other Website user posts.
            </p>
        </LegalSection>

        <LegalSection title="Monitoring and Enforcement; Termination">
            <p>We may:</p>
            <ol>
                <li>Remove or refuse to post any User Contributions for any reason.</li>
                <li>
                    Take any action regarding any User Contribution that we consider
                    necessary or appropriate, including if we believe that the User
                    Contribution violates the terms of service, including the Content
                    Standards, infringes any intellectual property right or other right of
                    any person or entity, threatens the personal safety of the Website’s
                    users or the public, or could create liability for the Company.
                </li>
                <li>
                    Disclose your identity or other information about you to any nonparty
                    who claims that material posted by you violates their rights,
                    including their intellectual property rights or their right to
                    privacy.
                </li>
                <li>
                    Take appropriate legal action, including without limitation, referral
                    to law enforcement, for any illegal or unauthorized use of the
                    Website.
                </li>
                <li>
                    Terminate or suspend your access to all or part of the Website for any
                    reason, including any violation of these terms of service.
                </li>
            </ol>
            <p class="p-2 border-2 border-black rounded">
                Without limiting the preceding, we may cooperate with any law
                enforcement authorities or court order requesting or directing us to
                disclose the identity or other information of anyone posting any
                materials on or through the Website. You hereby waive and shall
                indemnify the Company and its affiliates, licensees, and service
                providers against any claims resulting from any action taken by any of
                the preceding parties during, or taken because of, investigations by
                either those parties or law enforcement authorities.
            </p>
            <p>
                However, we cannot review all material before it is posted on the
                Website and cannot ensure prompt removal of objectionable material after
                it has been posted. Accordingly, we assume no liability for any action
                or inaction regarding transmissions, communications, or content provided
                by any user or nonparty. We have no liability or responsibility to
                anyone for performance or nonperformance of the activities described in
                this section.
            </p>
        </LegalSection>

        <LegalSection id="content-standards" title="Content Standards">
            <p>
                These content standards apply to all User Contributions and use of
                Interactive Services. User Contributions must, in their entirety, comply
                with all federal, state, local, and international laws and regulations.
                Without limiting the preceding, User Contributions must not:
            </p>
            <ol>
                <li>
                    Contain any material that is defamatory, obscene, indecent, abusive,
                    offensive, harassing, violent, hateful, inflammatory, or otherwise
                    objectionable.
                </li>
                <li>Contain sexually explicit or pornographic material.</li>
                <li>
                    Promote violence or discrimination based on race, sex, religion,
                    nationality, disability, sexual orientation, or age.
                </li>
                <li>
                    Infringe any patent, trademark, trade secret, copyright, or other
                    intellectual property or other rights of any other person.
                </li>
                <li>
                    Violate the legal rights (including the rights of publicity and
                    privacy) of others or contain any material that could give rise to any
                    civil or criminal liability under applicable laws or regulations or
                    that otherwise might be in conflict with these terms of service and
                    our
                    <Link href="/docs/privacy-policy" class="font-semibold text-malibu hover:underline">
                    Privacy Policy
                    </Link>
                    .
                </li>
                <li>Be likely to deceive any person.</li>
                <li>
                    Promote any illegal activity, or advocate, promote, or assist any
                    unlawful act, including, but not limited to, prostitution, sex
                    trafficking, or human trafficking.
                </li>
                <li>
                    Cause annoyance, inconvenience, or needless anxiety or be likely to
                    upset, embarrass, alarm, or annoy any other person.
                </li>
                <li>
                    Impersonate any person or misrepresent your identity or affiliation
                    with any person or organization.
                </li>
                <li>
                    Involve commercial activities or sales, including contests,
                    sweepstakes, and other sales promotions, barter, or advertising.
                </li>
                <li>
                    Give the impression that they emanate from or are endorsed by us or
                    any other person or entity, if this is not the case.
                </li>
            </ol>
        </LegalSection>
        <LegalSection title="Copyright Infringement">
            <p>
                If you believe that any User Contributions violate your copyright,
                please see our
                <Link href="/docs/DMCA" class="font-semibold text-malibu hover:underline">
                DMCA Policy
                </Link>
                for instructions on sending us a notice of copyright infringement. The
                Company’s policy is to terminate repeat infringers’ user accounts.
            </p>
        </LegalSection>
        <LegalSection title="Reliance on Information Posted">
            <p>
                The information presented on or through the Website is made available
                solely for general information purposes. We are not making any statement
                about the accuracy, completeness, or usefulness of this information. Any
                reliance you place on that information is strictly at your own risk. We
                will not be liable for any reliance placed on those materials by you or
                any other visitor to the Website, or by anyone who may be informed of
                any of its contents.
            </p>
            <p>
                This Website includes content provided by nonparties, including
                materials provided by other users, bloggers, and nonparty licensors,
                syndicators, aggregators, or reporting services. All statements or
                opinions expressed in these materials, and all articles and responses to
                questions and other content, other than the content provided by the
                Company, are solely the opinions and the responsibility of the person or
                entity providing those materials. These materials do not necessarily
                reflect the Company’s opinion. We are not responsible, or liable to you
                or any nonparty, for the content or accuracy of any materials provided
                by any nonparties.
            </p>
        </LegalSection>

        <LegalSection title="Your Safety">
            <p>
                We value the safety of all our users. It is your responsibility to do
                your own research and remain cautious. We strongly recommend that you
                check references before working with someone, especially if they are new
                to the Website or it’s your first time working together. We do not
                conduct criminal background checks on our users or otherwise inquire
                into the background of our users other than to verify our users’
                identity and age and validate their government-issued picture
                identification. We are not making any warranty about the conduct,
                identity, intentions, legitimacy, or veracity of users. Please
                immediately email us at support@spicycollab.com if you recognize or
                suspect a user has a criminal record.
            </p>
            <p class="p-2 border-2 border-black rounded">
                You acknowledge that you are solely responsible for your interactions
                with other users. Sex offender screenings and other tools do not
                guarantee your safety and are not a substitute for following sensible
                safety precautions. Always use your best judgment and take appropriate
                safety precautions when communicating with or meeting new people.
                Communications received through the Website, including automatic
                notifications sent by us, may result from users engaging the service for
                improper purposes, including fraud, abuse, harassment, or other such
                improper behavior. The US Federal Trade Commission has published
                <a href="https://www.ftc.gov/tips-advice/online-safety" target="_blank"
                    class="font-semibold text-malibu hover:underline">
                    advice on how to avoid modeling scams
                </a>
                . For more information about personal safety online, you can visit
                <a href="https://staysafeonline.org" target="_blank" class="font-semibold text-malibu hover:underline">
                    staysafeonline.org
                </a>
                ,
                <a href="https://onguardonline.gov" target="_blank" class="font-semibold text-malibu hover:underline">
                    onguardonline.gov
                </a>
                ,
                <a href="https://getsafeonline.org" target="_blank" class="font-semibold text-malibu hover:underline">
                    getsafeonline.or
                </a>
                g, or
                <a href="https://wiredsafety.org" class="font-semibold text-malibu hover:underline">
                    wiredsafety.org
                </a>
                .
            </p>
            <p>
                Though we strive to encourage a respectful user experience, we are not
                responsible for the conduct of any user on or off the Website. You must
                use caution in all interactions with other users, particularly if you
                decide to communicate off the Website or meet in person.
            </p>
        </LegalSection>

        <LegalSection title="Changes to the Website">
            <p>
                We may update the content on this Website on one or more occasions, but
                its content is not necessarily complete or up-to-date. Any material on
                the Website might be out of date at any given time, and we are not
                required to update that material.
            </p>
        </LegalSection>
        <LegalSection title="Information About You and Your Visits to the Website">
            <p>
                All information we collect on this Website is subject to our Privacy
                Policy. By using the Website, you consent to all actions taken by us
                regarding your information in compliance with the
                <Link href="/docs/privacy-policy" class="font-semibold text-malibu hover:underline">
                Privacy Policy
                </Link>
                .
            </p>
        </LegalSection>
        <p class="font-bold">Paid Accounts</p>
        <p class="italic font-semibold">Billing</p>
        <p class="p-2 border-2 border-black rounded">
            If you purchase any services, you must provide complete and accurate
            billing information, including a valid payment method. For paid
            subscriptions, we will automatically charge your payment method on each
            agreed-on periodic renewal until you cancel. You’re responsible for all
            applicable taxes, and we’ll charge tax when required. If your payment
            cannot be completed, we may downgrade your account or suspend your access
            to those services until payment is received.
        </p>
        <p class="italic font-semibold">Disputes</p>
        <p>
            If you believe there is an error in billing, you must contact us within 30
            days of the charge date at support@spicycollab.com. Failure to notify us
            within this period waives your right to dispute the charge.
        </p>
        <p class="italic font-semibold">Cancellation</p>
        <p>
            You can cancel your paid subscription at any time. Payments are
            non-refundable, except where required by law. These terms do not override
            any mandatory local laws regarding your cancellation rights.
        </p>
        <p class="italic font-semibold">Changes</p>
        <p>
            We may change our prices on one or more occasions. If we increase our
            subscription prices, we will give you at least 30 days’ notice and any
            price increase will take effect on your next renewal so that you can
            cancel if you do not agree to the price increase.
        </p>

        <LegalSection title="Linking to the Website and Social Media Features">
            <p>
                You may link to our homepage, on condition that you do so in a way that
                is fair and legal and does not damage our reputation or take advantage
                of it, but you must not establish a link in such a way as to suggest any
                form of association, approval, or endorsement on our part without our
                written consent.
            </p>
            <p>
                This Website may provide certain social media features that enable you
                to:
            </p>
            <ol>
                <li>
                    Link from your own or certain nonparty websites to certain content on
                    this Website.
                </li>
                <li>
                    Send emails or other communications with certain content, or links to
                    certain content, on this Website.
                </li>
                <li>
                    Cause limited portions of content on this Website to be displayed or
                    appear to be displayed on your own or certain nonparty websites.
                </li>
            </ol>
            <p>
                You may use these features solely as they are provided by us, solely
                regarding the content they are displayed with, and otherwise in
                accordance with any additional terms we provide regarding those
                features. Subject to the preceding, you must not:
            </p>
            <ol>
                <li>Establish a link from any website that is not owned by you.</li>
                <li>
                    Cause the Website or portions of it to be displayed on, or appear to
                    be displayed by, any other site, for example, framing, deep linking,
                    or in-line linking.
                </li>
                <li>Link to any part of the Website other than the homepage.</li>
                <li>
                    Otherwise take any action regarding the materials on this Website that
                    is inconsistent with any other provision of these terms of service.
                </li>
            </ol>
            <p>
                The website from which you are linking, or on which you make certain
                content accessible, must comply with the
                <Link href="#content-standards" class="font-semibold text-malibu hover:underline">
                Content Standards
                </Link>
                set out in these terms of service.
            </p>
            <p>
                You must cooperate with us in causing any unauthorized framing or
                linking immediately to stop. We may withdraw linking permission without
                notice.
            </p>
            <p>
                We may disable all or any social media features and any links without
                notice.
            </p>
        </LegalSection>
        <LegalSection title="Links from the Website">
            <p>
                If the Website contains links to other sites and resources provided by
                nonparties, these links are provided for your convenience only. This
                includes links contained in advertisements, including banner
                advertisements and sponsored links. We have no control over the contents
                of those sites or resources and accept no responsibility for them or for
                any loss or damage that might arise from your use of them. If you decide
                to access any of the nonparty websites linked to this Website, you do so
                entirely at your own risk and subject to the terms for those websites.
            </p>
        </LegalSection>
        <LegalSection title="Geographic Restrictions">
            <p>
                The Website’s owner is based in the State of Nevada in the United
                States. We make no claims that the Website or any of its content is
                accessible or appropriate outside the United States. Access to the
                Website might not be legal by certain persons or in certain countries.
                If you access the Website from outside the United States, you do so on
                your own initiative and are responsible for compliance with local laws.
            </p>
            <div class="p-2 border-2 border-black rounded">
                <p class="font-semibold" id="warranty-disclaimers">
                    Warranty Disclaimers
                </p>
                <p>
                    You understand that we cannot and do not guarantee or warrant that
                    files available for downloading from the internet or the Website will
                    be free of viruses or other destructive code. You are responsible for
                    implementing sufficient procedures and checkpoints to satisfy your
                    particular requirements for anti-virus protection and accuracy of data
                    input and output, and for maintaining a means external to our Website
                    for any reconstruction of any lost data. To the extent provided by
                    law, we will not be liable for any loss or damage caused by a
                    distributed denial-of-service attack, viruses, or other
                    technologically harmful material that might infect your computer
                    equipment, computer programs, data, or other proprietary material due
                    to your use of the Website or any services or items obtained through
                    the Website or your downloading of any material posted on it, or on
                    any website linked to it.
                </p>
                <p>
                    Your use of the Website, its content, and any services or items
                    obtained through the Website is at your own risk. The Website, its
                    content, and any services or items obtained through the Website are
                    provided “as is” and “as available,” without any warranties, either
                    express or implied. Neither the Company nor any person associated with
                    the Company is making any warranty regarding the completeness,
                    security, reliability, quality, accuracy, or availability of the
                    Website. Without limiting the preceding, neither the Company nor
                    anyone associated with the Company states that the Website, its
                    content, or any services or items obtained through the Website will be
                    accurate, reliable, error-free, or uninterrupted, that defects will be
                    corrected, that our Website or the server that makes it available are
                    free of viruses or other harmful components, or that the Website or
                    any services or items obtained through the Website will otherwise meet
                    your needs or expectations.
                </p>
                <p>
                    To the extent provided by law, the Company is not making any warranty,
                    whether express or implied, statutory, or otherwise, including but not
                    limited to any warranty of merchantability, noninfringement, and
                    fitness for particular purpose.
                </p>
                <p>
                    The preceding does not affect any warranties that cannot be excluded
                    or limited under law.
                </p>
                <p class="font-semibold" id="limitation-of-liability">
                    Limitation of Liability
                </p>
                <p>
                    To the extent provided by law, in no event will the collective
                    liability of the Company and its subsidiaries and affiliates, and
                    their licensors, service providers, employees, agents, officers, and
                    directors, to any party (regardless of the form of action, whether in
                    contract, tort, or otherwise) exceed the greater of $250 or the amount
                    you have paid to the Company for the service out of which liability
                    arose.
                </p>
                <p>
                    The limitation of liability set out above does not apply to liability
                    resulting from our gross negligence or willful misconduct or death or
                    bodily injury caused by services you purchase through the Website.
                </p>
                <p>
                    The preceding does not affect any liability that cannot be excluded or
                    limited under law.
                </p>
            </div>
        </LegalSection>
        <LegalSection title="Indemnification">
            <p>
                You shall indemnify the Company, its affiliates, licensors, and service
                providers, and its and their respective officers, directors, employees,
                contractors, agents, licensors, suppliers, successors, and assigns
                against any claims, liabilities, damages, judgments, awards, losses,
                costs, expenses, or fees (including reasonable attorneys’ fees) arising
                out of or relating to your violation of these terms of service or your
                use of the Website, including, but not limited to, your User
                Contributions, any use of the Website’s content, services, and products
                other than as expressly authorized in these terms of service, or your
                use of any information obtained from the Website.
            </p>
        </LegalSection>
        <p class="font-semibold" id="resolving-disputes">Resolving Disputes</p>
        <p class="italic font-semibold">Governing Law</p>
        <p>
            Nevada law governs all adversarial proceedings arising out of this
            agreement or the Website.
        </p>
        <p class="italic font-semibold">Equitable Remedies</p>
        <p>
            Each party acknowledges that (1) breach by either party of that party’s
            obligations under this agreement has the potential to cause irreparable
            harm for which damages would be an inadequate remedy and (2) if any such
            breach occurs or is threatened and might cause irreparable harm, each
            party would want a court to grant the other party an injunction, a
            restraining order, or any other equitable remedy, in each case without
            posting a bond or other security and without proof of actual damages.
        </p>
        <div class="p-2 border-2 border-black rounded">
            <p class="italic font-semibold" id="arbitration">
                Arbitration and Class Action Waiver
            </p>
            <p class="italic">Mandatory Arbitration</p>
            <p>
                You and we are agreeing to give up any rights to litigate claims in a
                court or before a jury, or to participate in a class action or
                representative action regarding a claim. Other rights that you would
                have if you went to court might also be unavailable or might be limited
                in arbitration.
            </p>
            <p class="italic">Arbitration Forum</p>
            <p>
                As the exclusive means of bringing adversarial proceedings to resolve
                any dispute arising out of this agreement or the Website (other than any
                proceeding brought by a party seeking an injunction, a restraining
                order, or any other equitable remedy to which that party is entitled
                under the Equitable Remedies provision), a party may demand that the
                dispute be resolved by arbitration administered by National Arbitration
                and Mediation (“NAM”) in accordance with its Comprehensive Dispute
                Resolution Rules and Procedures and, if applicable, Supplemental Rules
                for Mass Arbitration Filings, which are available at
                https://www.namadr.com/resources/rules-fees-forms/. Judgment on any
                award rendered in any arbitration may be entered in any court having
                jurisdiction. The activities described in these terms of service involve
                interstate commerce, and the Federal Arbitration Act governs the
                interpretation and enforcement of this arbitration provision and any
                arbitration.
            </p>
            <p class="italic">Arbitration Procedure</p>
            <p>
                Any arbitration begun in accordance with this arbitration provision must
                be conducted by one arbitrator.
            </p>
            <p>
                Any arbitration begun in accordance with this arbitration provision must
                be conducted virtually using Zoom or a virtual meeting platform that
                provides similar functionality. If virtual arbitration is not possible
                due to technical issues, because the arbitrator is unwilling or unable
                to attend the arbitration virtually, or for one or more other compelling
                reasons, the arbitration must be conducted in Las Vegas, Nevada, or any
                other place mutually agreed on by the parties.
            </p>
        </div>
        <div class="p-2 mt-4 border-2 border-black rounded">
            <p>
                The arbitrator must not award punitive damages in addition to
                compensatory damages. Each party hereby waives any right to recover any
                such damages in any arbitration.
            </p>
            <p>
                If you prevail on any claim that affords the prevailing party attorneys’
                fees, the arbitrator may award reasonable fees to you under the
                standards for fee shifting provided by law. We will not seek attorneys’
                fees and costs in arbitration unless the arbitrator determines that your
                claim is frivolous.
            </p>
            <p>
                The arbitrator will have the power to rule on any challenge to its own
                jurisdiction or to the validity or enforceability of any portion of the
                arbitration provision, except that the state or federal courts of Clark
                County, Nevada, have the authority to determine any dispute about
                enforceability or validity of the class action waiver.
            </p>
            <p class="italic">Small Claims Exception</p>
            <p>
                You may elect to pursue your claim in small-claims court rather than
                arbitration if you provide us with written notice of your intent to do
                so within 60 days of the date your claim arose. The small-claims court
                proceeding will be limited solely to your individual dispute or
                controversy.
            </p>
            <p class="italic">Class and Jury Trial Waivers</p>
            <p>
                The parties intend to arbitrate solely on an individual basis, and this
                agreement does not permit class arbitration or any claims brought as a
                plaintiff or class member in any class or representative proceeding. The
                arbitrator must not consolidate more than one person’s claims and must
                not otherwise preside over any form of a representative or class
                proceeding. Only individual relief is available. The parties shall sever
                and litigate in court any request for public injunctive relief after
                completing arbitration for the underlying claim and all other claims.
                This does not prevent either party from participating in a class-wide
                settlement. You and we knowingly and irrevocably waive any right to
                trial by jury in any action, proceeding, or counterclaim.
            </p>
            <p class="italic">Batch Arbitration</p>
            <p>
                If any provision of this Arbitration Agreement is found unenforceable,
                the unenforceable provision will be severed, and the remaining portions
                of this Arbitration Agreement will remain in force.
            </p>
            <p>
                If 25 or more claimants represented by the same or similar counsel file
                demands for arbitration raising substantially similar disputes within 90
                days of each other, then you and we agree that NAM will administer them
                in batches of up to 50 claimants each (“Batch”), unless there are less
                than 50 claimants in total or after batching, which will comprise a
                single Batch. NAM will administer each Batch as a single consolidated
                arbitration with one arbitrator, one set of arbitration fees, and one
                hearing held by videoconference or in a location decided by the
                arbitrator for each Batch. If any part of this section is found to be
                invalid or unenforceable as to a particular claimant or Batch, it will
                be severed and arbitrated in individual proceedings.
            </p>
            <p class="italic">Severability</p>
            <p>
                If any provision of this Arbitration Agreement is found unenforceable,
                the unenforceable provision will be severed, and the remaining portions
                of this Arbitration Agreement will remain in force.
            </p>
        </div>
        <p class="italic font-semibold">Jurisdiction</p>
        <p>
            If either party brings a proceeding seeking an injunction, a restraining
            order, or other equitable remedy to which that party is entitled under the
            Equitable Remedies provision, that party may bring that proceeding only in
            the United States District Court for the District of Nevada or, only if
            there is no federal subject matter jurisdiction, in a state court of
            Nevada sitting in Las Vegas.
        </p>
        <p>
            Each party hereby waives any claim that any proceeding brought in
            accordance with this provision has been brought in an inconvenient forum
            or that the venue of that proceeding is improper.
        </p>
        <p class="italic font-semibold">Recovering Expenses</p>
        <p>
            In an adversarial proceeding between the parties arising out of this
            agreement or the Website, the prevailing party will be entitled to recover
            from the other party, in addition to any other relief awarded, all
            expenses that the prevailing party incurs in those proceedings, including
            legal fees and expenses.
        </p>
        <p class="italic font-semibold">Time Limit to File Claims</p>
        <p>
            Apart from proceedings arising from a claim for indemnification, neither
            party may initiate adversarial proceedings to resolve any dispute arising
            out of this agreement or access or use of the Website more than one year
            after the date that dispute arose.
        </p>
        <LegalSection title="Waiver and Severability">
            <p>
                No waiver of any provision of this agreement will be effective unless it
                is in writing and signed by the party granting the waiver. No failure or
                delay in exercising any right or remedy under this agreement will
                operate as a waiver of that right or remedy. A waiver granted on one
                occasion will not operate as a waiver on future occasions.
            </p>
            <p>
                If any provision of these terms of service is held by a court or other
                tribunal of competent jurisdiction to be invalid, illegal, or
                unenforceable for any reason, that provision will be eliminated or
                limited to the minimum extent so that the remaining provisions of the
                terms of service will continue in full effect.
            </p>
        </LegalSection>
        <LegalSection title="Entire Agreement">
            <p>
                These terms of service constitute the entire understanding between the
                parties regarding the Website. You acknowledge that because you have not
                relied on, and will not be relying on, any statements made by the
                Company regarding the Website, you will have no basis for bringing any
                claim for fraud in connection with any such statements.
            </p>
        </LegalSection>
        <LegalSection title="Consent to Electronic Communications">
            <p>
                By using the Website or providing your contact information, you consent
                to receive all notices, agreements, disclosures, and other
                communications (collectively, “Communications”) from the Company
                electronically. These Communications may include emails, messages within
                the Website, or notifications provided through other electronic means.
                You agree that all Communications we provide to you electronically
                satisfy any legal requirement that those communications be in writing.
            </p>
            <p>
                You are responsible for maintaining an up-to-date email address or
                contact information to receive Communications. If you wish to withdraw
                your consent to receive electronic Communications, you must notify us by
                emailing support@spicycollab.com. Withdrawing consent may result in the
                termination of your account, as electronic Communications are necessary
                for the use of the Website and its services.
            </p>
        </LegalSection>
        <LegalSection title="Your Comments and Concerns">
            <p>
                This Website is operated by MMT Dev LLC, 9712 Saint Claude Avenue, Las
                Vegas, Nevada 89148.
            </p>
            <p>
                All notices of copyright infringement claims should be sent to the
                copyright agent designated in our
                <Link href="/docs/DMCA" class="font-semibold hover:underine text-malibu">
                DMCA Policy
                </Link>
                in the manner and by the means set out in it.
            </p>
            <p>
                All other feedback, comments, requests for technical support, and other
                communications relating to the Website should be directed to:
                <a mailto="support@spicycollab.com" class="font-semibold text-malibu hover:underline">
                    support@spicycollab.com
                </a>
                .
            </p>
        </LegalSection>
    </LegalPageLayout>
</template>

<style scoped>
ol {
    @apply list-inside list-disc pl-6;
}

p {
    @apply mt-4;
}
</style>
